// src/pages/carbon-offset-dashboard/components/SustainabilityGoals.jsx
import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const SustainabilityGoals = ({ goals, currentData, onGoalUpdate }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedGoals, setEditedGoals] = useState(goals);

  const handleSaveGoals = () => {
    onGoalUpdate(editedGoals);
    setIsEditMode(false);
  };

  const handleCancelEdit = () => {
    setEditedGoals(goals);
    setIsEditMode(false);
  };

  const calculateProgress = (current, target) => {
    if (target === 0) return 0;
    return Math.min((current / target) * 100, 100);
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-success';
    if (progress >= 60) return 'bg-primary';
    if (progress >= 40) return 'bg-warning';
    return 'bg-error';
  };

  const getProgressTextColor = (progress) => {
    if (progress >= 80) return 'text-success';
    if (progress >= 60) return 'text-primary';
    if (progress >= 40) return 'text-warning';
    return 'text-error';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDaysUntilTarget = (targetDate) => {
    const now = new Date();
    const target = new Date(targetDate);
    const diffTime = target - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const monthlyOffsetProgress = calculateProgress(currentData.offsetPurchased.current, goals.monthlyOffsetTarget);
  const offsetPercentageProgress = calculateProgress(currentData.offsetPercentage.current, goals.offsetPercentage);
  const daysUntilCarbonNeutral = getDaysUntilTarget(goals.carbonNeutralityTarget);

  const progressItems = [
    {
      id: 'monthly-offset',
      title: 'Monthly Offset Target',
      current: currentData.offsetPurchased.current,
      target: goals.monthlyOffsetTarget,
      unit: 'tonnes CO2e',
      progress: monthlyOffsetProgress,
      icon: 'Target',
      description: 'Monthly carbon offset goal'
    },
    {
      id: 'offset-percentage',
      title: 'Offset Coverage Goal',
      current: currentData.offsetPercentage.current,
      target: goals.offsetPercentage,
      unit: '%',
      progress: offsetPercentageProgress,
      icon: 'Shield',
      description: 'Percentage of emissions to offset'
    }
  ];

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-text-primary">Sustainability Goals</h3>
          <p className="text-sm text-text-secondary mt-1">
            Track progress towards your sustainability targets
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          {isEditMode ? (
            <>
              <button
                onClick={handleCancelEdit}
                className="p-2 text-text-secondary hover:text-primary hover:bg-primary-50 rounded-md transition-colors duration-150"
                title="Cancel editing"
              >
                <Icon name="X" size={16} />
              </button>
              <button
                onClick={handleSaveGoals}
                className="p-2 text-text-secondary hover:text-success hover:bg-success-50 rounded-md transition-colors duration-150"
                title="Save goals"
              >
                <Icon name="Check" size={16} />
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditMode(true)}
              className="p-2 text-text-secondary hover:text-primary hover:bg-primary-50 rounded-md transition-colors duration-150"
              title="Edit goals"
            >
              <Icon name="Edit" size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Progress Items */}
      <div className="space-y-6">
        {progressItems.map((item) => (
          <div key={item.id} className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Icon name={item.icon} size={16} className="text-primary" />
                <span className="text-sm font-medium text-text-primary">{item.title}</span>
              </div>
              {!isEditMode && (
                <span className={`text-sm font-medium ${getProgressTextColor(item.progress)}`}>
                  {item.progress.toFixed(1)}%
                </span>
              )}
            </div>
            
            {isEditMode ? (
              <div className="space-y-2">
                <label className="block text-xs text-text-secondary uppercase tracking-wide">
                  Target ({item.unit})
                </label>
                <input
                  type="number"
                  value={item.id === 'monthly-offset' ? editedGoals.monthlyOffsetTarget : editedGoals.offsetPercentage}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    setEditedGoals(prev => ({
                      ...prev,
                      [item.id === 'monthly-offset' ? 'monthlyOffsetTarget' : 'offsetPercentage']: value
                    }));
                  }}
                  step={item.unit === '%' ? '1' : '0.1'}
                  min="0"
                  max={item.unit === '%' ? '100' : undefined}
                  className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-secondary">
                    {item.current.toFixed(1)} / {item.target} {item.unit}
                  </span>
                  <span className="text-text-secondary">
                    {item.target > item.current ? `${(item.target - item.current).toFixed(1)} ${item.unit} remaining` : 'Target achieved!'}
                  </span>
                </div>
                
                <div className="w-full bg-surface-100 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-500 ${getProgressColor(item.progress)}`}
                    style={{ width: `${Math.min(item.progress, 100)}%` }}
                  ></div>
                </div>
              </>
            )}
            
            <p className="text-xs text-text-secondary">{item.description}</p>
          </div>
        ))}

        {/* Carbon Neutrality Target */}
        <div className="space-y-3 pt-4 border-t border-border">
          <div className="flex items-center space-x-2">
            <Icon name="Calendar" size={16} className="text-primary" />
            <span className="text-sm font-medium text-text-primary">Carbon Neutrality Target</span>
          </div>
          
          {isEditMode ? (
            <div className="space-y-2">
              <label className="block text-xs text-text-secondary uppercase tracking-wide">
                Target Date
              </label>
              <input
                type="date"
                value={editedGoals.carbonNeutralityTarget}
                onChange={(e) => setEditedGoals(prev => ({ ...prev, carbonNeutralityTarget: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          ) : (
            <>
              <div className="bg-primary-50 rounded-lg p-4 border border-primary-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-primary">
                    Target: {formatDate(goals.carbonNeutralityTarget)}
                  </span>
                  <span className={`text-sm font-medium ${
                    daysUntilCarbonNeutral > 365 ? 'text-success' :
                    daysUntilCarbonNeutral > 180 ? 'text-warning' : 'text-error'
                  }`}>
                    {daysUntilCarbonNeutral > 0 ? `${daysUntilCarbonNeutral} days remaining` : 'Target passed'}
                  </span>
                </div>
                <p className="text-xs text-primary">
                  Achieve net-zero carbon emissions across all operations
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Recommendations */}
      {!isEditMode && (
        <div className="mt-6 pt-4 border-t border-border">
          <div className="flex items-center space-x-2 mb-3">
            <Icon name="Lightbulb" size={16} className="text-warning" />
            <span className="text-sm font-medium text-text-secondary uppercase tracking-wide">Recommendations</span>
          </div>
          
          <div className="space-y-2">
            {monthlyOffsetProgress < 80 && (
              <div className="flex items-start space-x-2">
                <Icon name="ArrowRight" size={12} className="text-primary mt-1 flex-shrink-0" />
                <p className="text-xs text-text-secondary">
                  Increase monthly offset purchases by {(goals.monthlyOffsetTarget - currentData.offsetPurchased.current).toFixed(1)} tonnes to reach your target
                </p>
              </div>
            )}
            
            {offsetPercentageProgress < 80 && (
              <div className="flex items-start space-x-2">
                <Icon name="ArrowRight" size={12} className="text-primary mt-1 flex-shrink-0" />
                <p className="text-xs text-text-secondary">
                  Focus on offsetting {(goals.offsetPercentage - currentData.offsetPercentage.current).toFixed(1)}% more of your emissions
                </p>
              </div>
            )}
            
            {daysUntilCarbonNeutral < 365 && daysUntilCarbonNeutral > 0 && (
              <div className="flex items-start space-x-2">
                <Icon name="ArrowRight" size={12} className="text-warning mt-1 flex-shrink-0" />
                <p className="text-xs text-text-secondary">
                  Carbon neutrality target is approaching. Consider accelerating offset purchases and emission reduction initiatives
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SustainabilityGoals;